import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { getQuiz } from "utils/quizThunk"; // Import schedule thunk
import {  schedule } from "utils/scheduleThunk"; // Import schedule thunk
import toast from "react-hot-toast";

const validationSchema = Yup.object({
  quizId: Yup.string().required("Quiz selection is required"),
  title: Yup.string().required("Exam title is required"),
  date: Yup.date()
    .required("Exam date is required"),
    // .min(new Date(), "Date cannot be in the past"),
    resultDate: Yup.date()
    .required("Result date is required")
    .min(new Date(), "Date cannot be in the past"),
    questions:Yup.number().required("No of Questions is required")
    .min(1, "No of Questions  must be at least 1 ")
    .integer("No of Questions  must be an integer"),
  time: Yup.string().required("Exam time is required"),
  duration: Yup.number()
    .required("Duration is required")
    .min(1, "Duration must be at least 1 minute")
    .integer("Duration must be an integer"),
});

const initialValues = {
  quizId: "",
  title: "",
  date: "",
  time: "",
  duration: "", 
  questions:"",
  resultDate:""// Added duration
};

const AddExamModal = ({ handleSubmit, closeModal, onExamChange }) => {
  const dispatch = useDispatch();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch quizzes on mount
  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await dispatch(getQuiz({}));
        if (getQuiz.fulfilled.match(result)) {
          console.log("Fetched quizzes:", result.payload.quizzes);
          setQuizzes(result.payload.quizzes || []);
        } else {
          throw new Error(result.payload?.message || "Failed to fetch quizzes");
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
  }, [dispatch]);

  // Prevent form submission on Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      console.log("Enter key prevented from submitting form");
    }
  };

  // Handle form submission
  const handleFormSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setError(null);
    try {
      // Map form values to schema
      const payload = {
        quizId: values.quizId,
        examDate: values.date,
        resultDate: values.resultDate,
        examTime: values.time,
        description: values.title,
        questions:Number(values.questions),
        duration: Number(values.duration), // Convert to number
        isSchedule: true, // Set as per schema default
        status: "not-completed", // Set default status
      };
      console.log("Submitting exam with payload:", payload);

      const result = await dispatch(schedule(payload));
      if (schedule.fulfilled.match(result)) {
        toast.success("Exam scheduled successfully!");
        resetForm();
        closeModal();
        onExamChange()
      } else {
        throw new Error(result.payload?.message || "Failed to schedule exam");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" id="add_exam">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="fw-bold">Add New Exam</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              onClick={closeModal}
              aria-label="Close modal"
            />
          </div>
          <div className="modal-body">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
              validateOnChange
              validateOnBlur
            >
              {({ values, touched, errors }) => (
                <Form id="add-exam-form" className="row" onKeyDown={handleKeyDown}>
                  <div className="mb-3 col-12">
                    <label className="form-label" htmlFor="quizId">
                      Select Quiz
                    </label>
                    <Field
                      as="select"
                      name="quizId"
                      id="quizId"
                      className="form-control form-select"
                      aria-label="Select quiz"
                    >
                      <option value="">Select a Quiz</option>
                      {quizzes.map((quiz) => (
                        <option key={quiz._id} value={quiz._id}>
                          {quiz.quizTitle}
                        </option>
                      ))}
                    </Field>
                    {loading && <div>Loading quizzes...</div>}
                    {error && <div className="text-danger">{error}</div>}
                    {touched.quizId && errors.quizId && (
                      <div className="text-danger">{errors.quizId}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="title">
                      Exam Title
                    </label>
                    <Field
                      type="text"
                      name="title"
                      id="title"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam title"
                    />
                    {touched.title && errors.title && (
                      <div className="text-danger">{errors.title}</div>
                    )}
                  </div>
                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="duration">
                      No of Questions
                    </label>
                    <Field
                      type="number"
                      name="questions"
                      id="questions"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="No of Questions"
                    />
                    {touched.questions && errors.questions && (
                      <div className="text-danger">{errors.questions}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="date">
                      Exam Date
                    </label>
                    <Field
                      type="date"
                      name="date"
                      id="date"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam date"
                    />
                    {touched.date && errors.date && (
                      <div className="text-danger">{errors.date}</div>
                    )}
                  </div>
                   <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="time">
                      Exam Start Time
                    </label>
                    <Field
                      type="time"
                      name="time"
                      id="time"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam time"
                    />
                    {touched.time && errors.time && (
                      <div className="text-danger">{errors.time}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="duration">
                     Exam Duration (in minutes)
                    </label>
                    <Field
                      type="number"
                      name="duration"
                      id="duration"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam duration"
                    />
                    {touched.duration && errors.duration && (
                      <div className="text-danger">{errors.duration}</div>
                    )}
                  </div>
                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="date">
                      Result Announcement Date
                    </label>
                    <Field
                      type="date"
                      name="resultDate"
                      id="resultDate"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Result date"
                    />
                    {touched.resultDate && errors.resultDate && (
                      <div className="text-danger">{errors.resultDate}</div>
                    )}
                  </div>

                 

                  <div className="modal-footer mt-3">
                    <button
                      type="button"
                      className="btn bg-gray-100 rounded-pill mx-2"
                      data-bs-dismiss="modal"
                      onClick={closeModal}
                      aria-label="Cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-secondary rounded-pill"
                      disabled={Object.keys(errors).length > 0 || loading}
                      aria-label="Add exam"
                    >
                      {loading ? "Scheduling..." : "Add Exam"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExamModal;